import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_TENANT, TENANTS as SEED_TENANTS, type Tenant } from "@/lib/tenants";

/**
 * Loads the live tenants list from Lovable Cloud and exposes it via
 * context. Persists the last good list in localStorage so cold loads
 * don't flash empty before the network resolves.
 *
 * Fallback order on cold mount:
 *   1. localStorage cache (instant)
 *   2. background fetch from public.tenants (replaces cache + state)
 *   3. SEED_TENANTS from src/lib/tenants.ts (compiled in, used when
 *      both cache and network are unavailable)
 *
 * Adding a new tenant in the admin UI propagates here on next mount
 * (next time the user navigates / hard-refreshes). The Cloudflare
 * Worker has its own 5-minute edge cache for SSR meta tags.
 */

const CACHE_KEY = "app:tenants:v1";

interface CachedShape {
  tenants: Tenant[];
  cachedAt: number;
}

interface TenantsCtxValue {
  tenants: Tenant[];
  loading: boolean;
  /** Re-read the tenants from the database (after an admin edits one). */
  refresh: () => Promise<void>;
}

const TenantsCtx = createContext<TenantsCtxValue>({
  tenants: SEED_TENANTS,
  loading: false,
  refresh: async () => {},
});

const readCache = (): Tenant[] | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedShape;
    if (!Array.isArray(parsed.tenants) || parsed.tenants.length === 0) return null;
    return parsed.tenants;
  } catch {
    return null;
  }
};

const writeCache = (tenants: Tenant[]) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ tenants, cachedAt: Date.now() } satisfies CachedShape),
    );
  } catch {
    /* localStorage may be unavailable (private mode, quota), ignore */
  }
};

// Map snake_case DB rows to the camelCase Tenant shape used by the app.
type TenantRow = {
  id: string;
  hosts: string[];
  site_name: string;
  city_label: string;
  region_label: string;
  site_url: string;
  areas: string[];
  area_slugs: string[];
  primary_area_slug: string;
  og_image: string | null;
  country: string | null;
  address_region: string | null;
  contact_email: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  whatsapp: string | null;
  street_address: string | null;
  postal_code: string | null;
  geo_lat: number | null;
  geo_lng: number | null;
};

const fromRow = (r: TenantRow): Tenant => {
  // Pre-migration rows won't have the contact/social/geo columns yet; fall
  // back to the compiled seed for that same tenant so the live site (e.g.
  // Santa Teresa's footer socials + contact email) keeps working until the
  // migration lands, and local dev works without it.
  const seed = SEED_TENANTS.find((t) => t.id === r.id);
  return {
    id: r.id,
    hosts: r.hosts,
    siteName: r.site_name,
    cityLabel: r.city_label,
    regionLabel: r.region_label,
    siteUrl: r.site_url,
    areas: r.areas,
    areaSlugs: r.area_slugs,
    primaryAreaSlug: r.primary_area_slug,
    ogImage: r.og_image ?? undefined,
    country: r.country ?? DEFAULT_TENANT.country,
    addressRegion: r.address_region ?? DEFAULT_TENANT.addressRegion,
    contactEmail: r.contact_email ?? seed?.contactEmail,
    instagramUrl: r.instagram_url ?? seed?.instagramUrl,
    facebookUrl: r.facebook_url ?? seed?.facebookUrl,
    whatsapp: r.whatsapp ?? seed?.whatsapp,
    streetAddress: r.street_address ?? seed?.streetAddress,
    postalCode: r.postal_code ?? seed?.postalCode,
    geoLat: r.geo_lat ?? seed?.geoLat,
    geoLng: r.geo_lng ?? seed?.geoLng,
  };
};

export const TenantsProvider = ({ children }: { children: ReactNode }) => {
  // Render-first: synchronous cache OR seed so the first paint isn't blank.
  const [tenants, setTenants] = useState<Tenant[]>(() => readCache() ?? SEED_TENANTS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("tenants" as never)
      // select * (not an explicit column list) so a row that pre-dates the
      // contact/social/geo migration, or a frontend deployed before that
      // migration runs, never errors on a missing column.
      .select("*")
      .is("archived_at", null);
    if (error || !data || data.length === 0) {
      setLoading(false);
      return;
    }
    const next = (data as unknown as TenantRow[]).map(fromRow);
    setTenants(next);
    writeCache(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo(() => ({ tenants, loading, refresh: load }), [tenants, loading, load]);
  return <TenantsCtx.Provider value={value}>{children}</TenantsCtx.Provider>;
};

export const useTenants = (): TenantsCtxValue => useContext(TenantsCtx);

/** Helper for resolvers that need a tenant without the React context. */
export { DEFAULT_TENANT };
